'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import IconButton from '@savvy-kit/components/icon-button';
import { cn } from '@savvy-kit/lib/utils';
import { TagRoot } from '@savvy-kit/patterns/tag';
import { isNil } from 'es-toolkit';
import { ChevronDownIcon, PencilIcon, Trash2Icon } from 'lucide-react';

import { SaveViewDialog } from './save-view-dialog';

type SavedView = {
  readonly id: string;
  readonly name: string;
};

type SavedViewsProps<TSavedView extends SavedView> = {
  readonly savedViews: TSavedView[];
  readonly activeViewId: string | null;
  readonly isModified: boolean;
  readonly onLoadView: (view: TSavedView) => void;
  readonly onClearView: () => void;
  readonly onRenameView: (id: string, newName: string) => void;
  readonly onDeleteView: (id: string) => void;
  readonly renameDialogTitle?: string;
};

export function SavedViews<TSavedView extends SavedView>({
  savedViews,
  activeViewId,
  isModified,
  onLoadView,
  onClearView,
  onRenameView,
  onDeleteView,
  renameDialogTitle = 'Rename view',
}: SavedViewsProps<TSavedView>) {
  const [renamingView, setRenamingView] = useState<TSavedView | null>(null);
  const [visibleCount, setVisibleCount] = useState(savedViews.length);

  const containerRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const overflowBtnRef = useRef<HTMLDivElement>(null);
  const chipWidthCache = useRef<number[]>([]);

  useLayoutEffect(() => {
    chipRefs.current = chipRefs.current.slice(0, savedViews.length);

    const measureAndCalcVisible = () => {
      const container = containerRef.current;
      const overflowBtn = overflowBtnRef.current;
      if (isNil(container)) return;

      chipRefs.current.forEach((el) => {
        if (!isNil(el)) el.style.display = '';
      });
      if (!isNil(overflowBtn)) {
        overflowBtn.style.display = '';
        overflowBtn.style.visibility = 'hidden';
      }

      chipWidthCache.current = chipRefs.current.map((el) => (!isNil(el) ? el.offsetWidth : 0));

      const containerWidth = container.offsetWidth;
      const overflowBtnWidth = !isNil(overflowBtn) ? overflowBtn.offsetWidth + 8 : 0;

      const gap = 8;
      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < chipWidthCache.current.length; i++) {
        const chipW = (chipWidthCache.current[i] ?? 0) + (i > 0 ? gap : 0);
        const remaining = savedViews.length - i - 1;
        const needsOverflow = remaining > 0;
        const reservedForBtn = needsOverflow ? overflowBtnWidth : 0;

        if (usedWidth + chipW + reservedForBtn <= containerWidth) {
          usedWidth += chipW;
          count++;
        } else {
          break;
        }
      }

      setVisibleCount(count);

      chipRefs.current.forEach((el, i) => {
        if (!isNil(el)) el.style.display = i < count ? '' : 'none';
      });

      if (!isNil(overflowBtn)) {
        const needsOverflowBtn = count < savedViews.length;
        overflowBtn.style.display = needsOverflowBtn ? '' : 'none';
        overflowBtn.style.visibility = needsOverflowBtn ? 'visible' : 'hidden';
        overflowBtn.style.pointerEvents = needsOverflowBtn ? '' : 'none';
      }
    };

    measureAndCalcVisible();

    const observer = new ResizeObserver(measureAndCalcVisible);
    if (!isNil(containerRef.current)) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [savedViews]);

  if (savedViews.length === 0) return null;

  const hiddenViews = savedViews.slice(visibleCount);

  return (
    <>
      <div ref={containerRef} className="flex flex-nowrap overflow-hidden gap-2 mb-6 mt-2 items-center">
        {savedViews.map((view, index) => {
          const isActive = view.id === activeViewId;
          const showModified = isActive && isModified;

          return (
            <div
              key={view.id}
              ref={(el) => {
                chipRefs.current[index] = el;
              }}
              className="shrink-0"
            >
              <TagRoot
                render={<div />}
                className="overflow-visible p-0 gap-0 h-auto hover:bg-component-hover transition-colors"
              >
                <button
                  type="button"
                  aria-pressed={isActive}
                  className="flex items-center gap-1.5 py-1.5 pl-3 pr-1 text-sm"
                  onClick={() => (isActive ? onClearView() : onLoadView(view))}
                >
                  <span
                    className={cn(
                      'inline-block size-2 rounded-full shrink-0',
                      isActive && !showModified ? 'bg-[var(--primary)]' : 'bg-[var(--border-light)]',
                    )}
                  />
                  {showModified && <span className="sr-only">(modified)</span>}
                  {view.name}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={`Options for ${view.name}`}
                        className="mr-1 rounded-full"
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                  >
                    <ChevronDownIcon size={14} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setRenamingView(view)}>Rename</DropdownMenuItem>
                    <DropdownMenuItem className="text-[var(--destructive)]" onClick={() => onDeleteView(view.id)}>
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TagRoot>
            </div>
          );
        })}

        <div ref={overflowBtnRef} className="shrink-0" style={{ display: 'none', pointerEvents: 'none' }}>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <button
                  type="button"
                  aria-label="More saved views"
                  className="inline-flex items-center rounded-full border border-[var(--border-light)] bg-background px-3 py-1.5 text-sm hover:bg-component-hover transition-colors"
                />
              }
            >
              +{hiddenViews.length}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-w-56">
              {hiddenViews.map((view) => {
                const isActive = view.id === activeViewId;
                const showModified = isActive && isModified;
                return (
                  <DropdownMenuItem
                    key={view.id}
                    onClick={() => (isActive ? onClearView() : onLoadView(view))}
                    className="group flex items-center gap-2 pr-1"
                  >
                    <span
                      className="inline-block size-2 rounded-full shrink-0"
                      style={{ backgroundColor: isActive && !showModified ? 'var(--primary)' : 'var(--border-light)' }}
                    />
                    <span className="flex-1 truncate">{view.name}</span>
                    <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                      <button
                        type="button"
                        aria-label={`Rename ${view.name}`}
                        className="flex items-center justify-center rounded p-1 hover:bg-[var(--component-hover)]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenamingView(view);
                        }}
                      >
                        <PencilIcon size={12} className="text-[var(--muted-foreground)]" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete ${view.name}`}
                        className="flex items-center justify-center rounded p-1 hover:bg-[var(--component-hover)]"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteView(view.id);
                        }}
                      >
                        <Trash2Icon size={12} className="text-[var(--destructive)]" />
                      </button>
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <SaveViewDialog
        open={renamingView !== null}
        onOpenChange={(open) => {
          if (!open) setRenamingView(null);
        }}
        initialValue={renamingView?.name ?? ''}
        existingNames={savedViews.filter((v) => v.id !== renamingView?.id).map((v) => v.name)}
        labels={{
          title: renameDialogTitle,
          fieldLabel: 'Name',
          placeholder: 'e.g. My view',
          cancel: 'Cancel',
          save: 'Save',
          requiredError: 'Name is required.',
          maxLengthError: (maxLength) => `Name must be ${maxLength} characters or fewer.`,
          duplicateError: 'A view with this name already exists.',
        }}
        onSubmit={(newName) => {
          if (!isNil(renamingView)) onRenameView(renamingView.id, newName);
        }}
      />
    </>
  );
}

export type { SavedView, SavedViewsProps };
